import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/firebase-manager';
import { FaLightbulb, FaRocket, FaHeart, FaCog, FaBriefcase, FaCalendarAlt, FaBars, FaTimes, FaHome, FaShieldAlt, FaArrowRight, FaDownload, FaCalendarCheck } from 'react-icons/fa';
import appIcon from '../assets/CollectedAF_Icon copy.png';
import LeftSection from './LeftSection';
import CenterSection from './CenterSection';
import RightSection from './RightSection';
import DownloadSection from './DownloadSection';
import BookDemoSection from './BookDemoSection';
import HomeSection from './HomeSection';
import VenuesSection from './VenuesSection';
import HowItWorksSection from './HowItWorksSection';
import TechnologySection from './TechnologySection';
import CommunitySection from './CommunitySection';
import CareersSection from './CareersSection';
import PrivacyPolicySection from './PrivacyPolicySection';
import ChildSafetySection from './ChildSafetySection';
import EventsBanner, { BANNER_DISMISSED_KEY } from './EventsBanner';
import EventsSection from './rides/EventsSection';
import EventDetailSection from './rides/EventDetailSection';
import BookingConfirmationSection from './rides/BookingConfirmationSection';
import { useWebAppConfig } from '../context/webAppConfigContext';
import { track } from '../services/webAppTrack';
import type { ViewType, WebAppConfig } from '../services/webAppConfig';
import { getRoutes, HARDCODED_DEFAULT_VIEW, type RoutesConfig } from '../services/webAppConfig';

/* Inlined to avoid fork-ts-checker module resolution cache miss on new files */
const AMBIENT_ORBS = [
  { color: '#3b82f6', top: '8%',  left: '18%', size: 520, delay: 0,  duration: 22, drift: 0 },
  { color: '#6366f1', top: '62%', left: '72%', size: 620, delay: 3,  duration: 28, drift: 1 },
  { color: '#0ea5e9', top: '82%', left: '12%', size: 460, delay: 6,  duration: 24, drift: 2 },
  { color: '#fbbf24', top: '22%', left: '78%', size: 380, delay: 1,  duration: 30, drift: 0 },
  { color: '#14b8a6', top: '48%', left: '42%', size: 520, delay: 9,  duration: 26, drift: 1 },
  { color: '#2563eb', top: '92%', left: '55%', size: 440, delay: 4,  duration: 32, drift: 2 },
];
const AmbientOrbs: React.FC<{ intensity?: number }> = ({ intensity = 0.55 }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
    {AMBIENT_ORBS.map((orb, i) => (
      <div
        key={i}
        className={`absolute rounded-full orb-drift-${orb.drift}`}
        style={{
          top: orb.top, left: orb.left, width: `${orb.size}px`, height: `${orb.size}px`,
          marginLeft: `-${orb.size / 2}px`, marginTop: `-${orb.size / 2}px`,
          background: `radial-gradient(circle, ${orb.color}cc 0%, ${orb.color}00 70%)`,
          filter: 'blur(70px)', mixBlendMode: 'screen', opacity: intensity,
          animationDelay: `${orb.delay}s`, animationDuration: `${orb.duration}s`, willChange: 'transform',
        }}
      />
    ))}
  </div>
);

/** Map view to URL param value */
const VIEW_TO_PARAM: Record<ViewType, string> = {
  main: 'main',
  privacy: 'privacy',
  download: 'download',
  demo: 'demo',
  home: 'home',
  events: 'events',
  event: 'event',
  booked: 'booked',
  childSafety: 'childSafety',
};

/** Section param -> config.sections visibility key */
const SECTION_VISIBILITY: Record<string, keyof WebAppConfig['sections']> = {
  experience: 'showExperience',
  venues: 'showVenues',
  community: 'showCommunity',
  technology: 'showTechnology',
  careers: 'showCareers',
};

/** Lowercase URL param -> actual ViewType (handles camelCase views) */
const PARAM_TO_VIEW_MAP: Record<string, ViewType> = {
  main: 'main',
  privacy: 'privacy',
  download: 'download',
  demo: 'demo',
  home: 'home',
  events: 'events',
  event: 'event',
  booked: 'booked',
  childsafety: 'childSafety',
};

/** Parse URL param to view. Uses routes from config. Section names (e.g. careers, venues) as view = main + that section */
function paramToView(param: string | null, routes: RoutesConfig): ViewType | null {
  if (!param) return null;
  const v = param.toLowerCase();
  const mapped = PARAM_TO_VIEW_MAP[v];
  if (mapped) return mapped;
  if (routes.views.includes(v)) return 'main';
  if (routes.sections.includes(v)) return 'main';
  return null;
}

/** Resolve section param from view param (e.g. view=careers -> section careers) */
function getEffectiveSectionParam(viewParam: string | null, sectionParam: string | null, routes: RoutesConfig): string | null {
  const v = viewParam?.toLowerCase();
  const s = sectionParam?.toLowerCase();
  if (routes.sections.includes(v ?? '')) return v ?? null;
  return s && routes.sections.includes(s) ? s : null;
}

/** Get section index from URL param, respecting config visibility. Uses routes from config. */
function paramToSectionIndex(
  viewParam: string | null,
  sectionParam: string | null,
  routes: RoutesConfig,
  config: WebAppConfig
): number {
  const effective = getEffectiveSectionParam(viewParam, sectionParam, routes);
  if (!effective) return 0;

  const idx = routes.sections.indexOf(effective);
  if (idx < 0) return 0;

  const visibilityKey = SECTION_VISIBILITY[effective];
  const visible = visibilityKey ? !!config.sections[visibilityKey] : true;
  if (!visible) {
    const firstVisible = routes.sections.findIndex((s) => {
      const k = SECTION_VISIBILITY[s];
      return k ? !!config.sections[k] : true;
    });
    return Math.max(0, firstVisible);
  }
  return idx;
}

/** Derive showX/activeSection from view and optional section index */
function viewToState(view: ViewType, sectionIndex = 0) {
  return {
    showHome: view === 'home',
    showDownload: view === 'download',
    showBookDemo: view === 'demo',
    showPrivacyPolicy: view === 'privacy',
    showEvents: view === 'events',
    showEventDetail: view === 'event',
    showBooked: view === 'booked',
    showChildSafety: view === 'childSafety',
    activeSection: view === 'main' ? sectionIndex : 0,
    showVenues: view === 'main' && sectionIndex === 1,
  };
}

interface CollectedAFWebSiteProps {
  children?: React.ReactNode;
}

const CollectedAFWebSite: React.FC<CollectedAFWebSiteProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { config, configLoaded } = useWebAppConfig();

  const [videoUrl, setVideoUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [showDownload, setShowDownload] = useState(false);
  const [showBookDemo, setShowBookDemo] = useState(false);
  const [showHome, setShowHome] = useState(false);
  const [showVenues, setShowVenues] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [showBooked, setShowBooked] = useState(false);
  const [showChildSafety, setShowChildSafety] = useState(false);
  const [rideEventId, setRideEventId] = useState<string | null>(null);
  const [bookingState, setBookingState] = useState<any>(null);
  const [viewAppliedFromConfig, setViewAppliedFromConfig] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem(BANNER_DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try { localStorage.setItem(BANNER_DISMISSED_KEY, '1'); } catch { /* ignore */ }
  }, []);

  const centerSectionRef = useRef<HTMLDivElement>(null);
  const scrollToCenterSection = useCallback(() => {
    if (centerSectionRef.current) {
      centerSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  /** Current view for tracking */
  const getCurrentView = useCallback((): ViewType => {
    if (showPrivacyPolicy) return 'privacy';
    if (showChildSafety) return 'childSafety';
    if (showDownload) return 'download';
    if (showBookDemo) return 'demo';
    if (showHome) return 'home';
    if (showEvents) return 'events';
    if (showEventDetail) return 'event';
    if (showBooked) return 'booked';
    return 'main';
  }, [showPrivacyPolicy, showChildSafety, showDownload, showBookDemo, showHome, showEvents, showEventDetail, showBooked]);

  /** Navigate to a full-page view */
  const goToView = useCallback(
    (view: ViewType, params?: Record<string, string>) => {
      const s = viewToState(view);
      setShowHome(s.showHome);
      setShowDownload(s.showDownload);
      setShowBookDemo(s.showBookDemo);
      setShowPrivacyPolicy(s.showPrivacyPolicy);
      setShowChildSafety(s.showChildSafety);
      setShowEvents(s.showEvents);
      setShowEventDetail(s.showEventDetail);
      setShowBooked(s.showBooked);
      setShowVenues(false);
      setActiveSection(0);
      setSearchParams({ view: VIEW_TO_PARAM[view], ...(params || {}) }, { replace: true });
      track({ eventType: 'page_view', view });
      scrollToCenterSection();
    },
    [setSearchParams, scrollToCenterSection]
  );

  const routes = getRoutes(config);

  /** Navigate to main section (uses section param from config routes) */
  const goToSection = useCallback(
    (index: number) => {
      const sectionParam = routes.sections[index] ?? routes.sections[0];
      setActiveSection(index);
      setShowVenues(index === 1);
      setShowDownload(false);
      setShowBookDemo(false);
      setShowHome(false);
      setShowPrivacyPolicy(false);
      setShowChildSafety(false);
      setShowEvents(false);
      setShowEventDetail(false);
      setShowBooked(false);
      setSearchParams({ view: 'main', section: sectionParam }, { replace: true });
      track({ eventType: 'section_view', view: 'main', section: sectionParam });
      scrollToCenterSection();
    },
    [routes, setSearchParams, scrollToCenterSection]
  );

  /** Apply view/section. Priority: 1) URL params, 2) config.defaultView, 3) hardcoded fallback. Only runs after config loads. */
  useEffect(() => {
    if (!configLoaded) return;

    const viewParam = searchParams.get('view');
    const sectionParam = searchParams.get('section');
    const urlView = paramToView(viewParam, routes);
    const configView = config.defaultView;
    const view = urlView ?? configView ?? HARDCODED_DEFAULT_VIEW;

    const sectionIndex = view === 'main'
      ? paramToSectionIndex(viewParam, sectionParam, routes, config)
      : 0;
    const s = viewToState(view, sectionIndex);

    setShowHome(s.showHome);
    setShowDownload(s.showDownload);
    setShowBookDemo(s.showBookDemo);
    setShowPrivacyPolicy(s.showPrivacyPolicy);
    setShowChildSafety(s.showChildSafety);
    setShowEvents(s.showEvents);
    setShowEventDetail(s.showEventDetail);
    setShowBooked(s.showBooked);
    setShowVenues(s.showVenues);
    setActiveSection(s.activeSection);
    setViewAppliedFromConfig(true);

    if (view === 'event' || view === 'booked') {
      setRideEventId(searchParams.get('eventId'));
    }

    const effectiveSection = routes.sections[sectionIndex];
    if (!viewParam) {
      setSearchParams({ view: VIEW_TO_PARAM[view] }, { replace: true });
    } else if (routes.sections.includes(viewParam.toLowerCase())) {
      setSearchParams({ view: 'main', section: viewParam.toLowerCase() }, { replace: true });
    } else if (view === 'main' && sectionParam !== effectiveSection) {
      setSearchParams({ view: 'main', section: effectiveSection ?? routes.sections[0] }, { replace: true });
    }
    track({ eventType: 'page_view', view });
  }, [configLoaded, searchParams, config, routes, setSearchParams]);
  
  // Touch handling for mobile swipe
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    // Ignore touches on interactive elements
    if (target.closest('button') || target.closest('a') || target.closest('input') || target.closest('select')) {
      touchStartX.current = -1;
      return;
    }
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === -1) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === -1) {
      touchStartX.current = 0;
      touchEndX.current = 0;
      return;
    }
    
    if (!showHome && !showDownload && !showBookDemo && !showChildSafety) {
      const swipeDistance = touchStartX.current - touchEndX.current;
      const minSwipeDistance = 50;

      if (Math.abs(swipeDistance) > minSwipeDistance) {
        if (swipeDistance > 0) {
          // Swipe left - next section
          const nextSection = (activeSection + 1) % sections.length;
          setActiveSection(nextSection);
          setShowVenues(nextSection === 1);
        } else {
          // Swipe right - previous section
          const prevSection = activeSection === 0 ? sections.length - 1 : activeSection - 1;
          setActiveSection(prevSection);
          setShowVenues(prevSection === 1);
        }
      }
    }
    
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Navigation sections
  const sections = [
    { 
      id: 0, 
      title: 'The CollectedAF Experience', 
      icon: FaLightbulb,
      content: 'We turn real-life moments into real connections — instantly.',
      steps: [
        {
          number: 1,
          title: 'Walk past anyone',
          description: 'Your phone detects people nearby automatically.'
        },
        {
          number: 2,
          title: 'See live attributes',
          description: '#dogmom, #financeBro, #MarketingMajor, etc.'
        },
        {
          number: 3,
          title: 'Collect people → build your network',
          description: 'Each interaction becomes a permanent digital card.'
        }
      ]
    },
    { 
      id: 1, 
      title: 'For Businesses', 
      icon: FaRocket,
      content: 'Your event. Your audience. Live. One dashboard to see, reach, and engage every customer in real time.',
      features: [
        {
          title: 'Live event screen',
          description: 'A branded page your audience watches — with real-time grid, activity feed, and auto-cycling showcase.'
        },
        {
          title: 'Push promotions & announcements',
          description: 'Reach every attendee instantly with offers and live messages.'
        },
        {
          title: 'Real-time analytics',
          description: 'See who\'s there, how long they stay, and what they engage with.'
        },
        {
          title: 'Full CRM portal',
          description: 'Scene control, company management, promotions, and theme customization.'
        }
      ],
      dashboardFeatures: [
        'Scene Control',
        'Live Announcements',
        'Promotions',
        'Analytics',
        'Company Management'
      ]
    },
    { 
      id: 2, 
      title: 'Community', 
      icon: FaHeart,
      content: 'Join thousands of users building meaningful connections. Every person is an opportunity waiting to happen.'
    },
    { 
      id: 3, 
      title: 'Technology', 
      icon: FaCog,
      content: 'Powered by cutting-edge location technology and real-time data processing for instant connections.'
    },
    { 
      id: 4, 
      title: 'Careers / Brand Ambassadors', 
      icon: FaBriefcase,
      content: 'Join our team. We\'re looking for talented marketers to help grow CollectedAF.'
    },
  ];

  useEffect(() => {
    const fetchVideoUrl = async () => {
      try {
        const videoRef = ref(storage, 'videos/collectedAF-commercial1.0.mp4');
        const url = await getDownloadURL(videoRef);
        setVideoUrl(url);
      } catch (error) {
        console.error('Error fetching video:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideoUrl();
  }, []);

  // Generate animated dots grid - memoized to prevent infinite loops
  const [dots, setDots] = useState<JSX.Element[]>([]);

  useEffect(() => {
    const generateDots = () => {
      const newDots = [];
      const rows = Math.ceil(window.innerHeight / 40); // Reduced density
      const cols = Math.ceil(window.innerWidth / 40); // Reduced density
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const shouldAnimate = Math.random() > 0.8; // 20% of dots animate
          const delay = Math.random() * 3; // Random delay 0-3s
          
          newDots.push(
            <div
              key={`${row}-${col}`}
              className={shouldAnimate ? 'animated-dot' : ''}
              style={{
                position: 'absolute',
                top: `${row * 40}px`,
                left: `${col * 40}px`,
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                border: '.5px solid #04f540ff',
                backgroundColor: 'transparent',
                opacity: 0.1,
                animationDelay: shouldAnimate ? `${delay}s` : undefined,
              }}
            />
          );
        }
      }
      return newDots;
    };
    
    setDots(generateDots());
  }, []);

  if (!configLoaded || !viewAppliedFromConfig) {
    return (
      <div className="relative min-h-screen overflow-hidden flex items-center justify-center" style={{ background: 'radial-gradient(ellipse at 20% 50%, #f97316 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, #6366f1 0%, transparent 40%), radial-gradient(ellipse at 50% 80%, #1e3a8a 0%, transparent 50%), linear-gradient(135deg, #f97316 0%, #1e3a8a 50%, #0f172a 100%)' }}>
        <AmbientOrbs intensity={0.6} />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-WHITE"></div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: 'radial-gradient(ellipse at 20% 50%, #f97316 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, #6366f1 0%, transparent 40%), radial-gradient(ellipse at 50% 80%, #1e3a8a 0%, transparent 50%), linear-gradient(135deg, #f97316 0%, #1e3a8a 50%, #0f172a 100%)' }}>
        <AmbientOrbs intensity={0.55} />
        {/* Top banner: Free rides to [event] — dismissable, links to view=events */}
        <EventsBanner
          onGoToEvents={() => goToView('events')}
          onDismiss={dismissBanner}
          isDismissed={bannerDismissed}
        />

        {/* Animated Dotted Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          {dots}
        </div>

      {/* Top-Left Corner Icon - Desktop only */}
      <div className="hidden lg:block absolute top-8 left-8 z-20">
        <button
          onClick={() => config.sections.showHome && goToView('home')}
          className="bg-tesst rounded-lg p-2 shadow-lg hover:scale-110 transition-transform cursor-pointer"
        >
          <img 
            src={appIcon} 
            alt="CollectedAF Icon" 
            className="w-12 h-12"
          />
        </button>
      </div>

      {/* Mobile Header: Logo + CollectedAF + Hamburger/Close */}
      <div className="lg:hidden absolute top-8 left-8 right-8 z-50 flex items-center justify-between">
        <button
          onClick={() => config.sections.showHome && goToView('home')}
          className="flex items-center gap-3 focus:outline-none"
        >
          <div className="bg-white rounded-lg p-2 shadow-lg">
            <img src={appIcon} alt="CollectedAF Icon" className="w-12 h-12" />
          </div>
          <span
            className="text-2xl font-bold tracking-tight"
            style={{ color: '#0066cc' }}
          >
            CollectedAF
          </span>
        </button>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-WHITE p-2 hover:text-ORANGE transition-colors"
        >
          {mobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex flex-col overflow-hidden" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}>
          {/* Ambient glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.12), transparent 70%)', filter: 'blur(40px)' }} />

          {/* Scrollable nav area */}
          <nav className="flex flex-col gap-2 px-5 w-full mt-24 pb-4 overflow-y-auto flex-1 min-h-0">
            {/* Section label */}
            <div className="text-WHITE/30 text-[10px] font-semibold tracking-widest uppercase mb-1 pl-2">Navigate</div>

            {sections
              .filter((_, i) => {
                const visible = [config.sections.showExperience, config.sections.showVenues, config.sections.showCommunity, config.sections.showTechnology, config.sections.showCareers];
                return visible[i];
              })
              .map((section, idx) => {
                const MOBILE_ICON_COLORS = [
                  { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa', activeBg: 'rgba(59,130,246,0.25)', border: 'rgba(59,130,246,0.3)' },
                  { bg: 'rgba(16,185,129,0.15)', color: '#34d399', activeBg: 'rgba(16,185,129,0.25)', border: 'rgba(16,185,129,0.3)' },
                  { bg: 'rgba(236,72,153,0.15)', color: '#f472b6', activeBg: 'rgba(236,72,153,0.25)', border: 'rgba(236,72,153,0.3)' },
                  { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', activeBg: 'rgba(245,158,11,0.25)', border: 'rgba(245,158,11,0.3)' },
                  { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa', activeBg: 'rgba(139,92,246,0.25)', border: 'rgba(139,92,246,0.3)' },
                ];
                const Icon = section.icon;
                const sectionIndex = sections.indexOf(section);
                const isActive = activeSection === sectionIndex && !showHome && !showDownload && !showBookDemo && !showPrivacyPolicy && !showChildSafety && !showEvents;
                const clr = MOBILE_ICON_COLORS[idx % MOBILE_ICON_COLORS.length];
                return (
                  <button
                    key={sectionIndex}
                    onClick={() => {
                      goToSection(sectionIndex);
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 ${
                      isActive
                        ? 'bg-WHITE/[0.12] border border-WHITE/20 shadow-[0_0_16px_rgba(255,255,255,0.06)]'
                        : 'bg-WHITE/[0.04] border border-transparent'
                    }`}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border"
                      style={{
                        background: isActive ? clr.activeBg : clr.bg,
                        borderColor: isActive ? clr.border : 'transparent',
                      }}
                    >
                      <Icon size={16} style={{ color: clr.color }} />
                    </div>
                    <span className={`text-sm font-semibold transition-colors ${isActive ? 'text-WHITE' : 'text-WHITE/70'}`}>{section.title}</span>
                    {isActive && <FaArrowRight size={10} className="ml-auto text-WHITE/30 flex-shrink-0" />}
                  </button>
                );
              })}

            {/* Events */}
            <button
              onClick={() => {
                goToView('events');
                setMobileMenuOpen(false);
              }}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 ${
                showEvents
                  ? 'bg-WHITE/[0.12] border border-WHITE/20 shadow-[0_0_16px_rgba(255,255,255,0.06)]'
                  : 'bg-WHITE/[0.04] border border-transparent'
              }`}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border"
                style={{
                  background: showEvents ? 'rgba(6,182,212,0.25)' : 'rgba(6,182,212,0.15)',
                  borderColor: showEvents ? 'rgba(6,182,212,0.3)' : 'transparent',
                }}
              >
                <FaCalendarAlt size={16} style={{ color: '#22d3ee' }} />
              </div>
              <span className={`text-sm font-semibold transition-colors ${showEvents ? 'text-WHITE' : 'text-WHITE/70'}`}>Events</span>
              {showEvents && <FaArrowRight size={10} className="ml-auto text-WHITE/30 flex-shrink-0" />}
            </button>
            
            {/* Divider + Home/Privacy */}
            {(config.sections.showHome || config.sections.showPrivacyPolicy) && (
              <>
                <div className="relative h-px w-full my-2">
                  <div className="absolute inset-0 bg-WHITE/10" />
                  <div className="absolute inset-y-0 left-[15%] right-[15%]" style={{ background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.4), rgba(52,211,153,0.3), transparent)' }} />
                </div>
                {config.sections.showHome && (
                  <button
                    onClick={() => {
                      goToView('home');
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 ${
                      showHome
                        ? 'bg-WHITE/[0.12] border border-WHITE/20 shadow-[0_0_16px_rgba(255,255,255,0.06)]'
                        : 'bg-WHITE/[0.04] border border-transparent'
                    }`}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border"
                      style={{
                        background: showHome ? 'rgba(14,165,233,0.25)' : 'rgba(14,165,233,0.15)',
                        borderColor: showHome ? 'rgba(14,165,233,0.3)' : 'transparent',
                      }}
                    >
                      <FaHome size={16} style={{ color: '#38bdf8' }} />
                    </div>
                    <span className={`text-sm font-semibold transition-colors ${showHome ? 'text-WHITE' : 'text-WHITE/70'}`}>Home</span>
                    {showHome && <FaArrowRight size={10} className="ml-auto text-WHITE/30 flex-shrink-0" />}
                  </button>
                )}
                {config.sections.showPrivacyPolicy && (
                  <button
                    onClick={() => {
                      goToView('privacy');
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 ${
                      showPrivacyPolicy
                        ? 'bg-WHITE/[0.12] border border-WHITE/20 shadow-[0_0_16px_rgba(255,255,255,0.06)]'
                        : 'bg-WHITE/[0.04] border border-transparent'
                    }`}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border"
                      style={{
                        background: showPrivacyPolicy ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.15)',
                        borderColor: showPrivacyPolicy ? 'rgba(99,102,241,0.3)' : 'transparent',
                      }}
                    >
                      <FaShieldAlt size={16} style={{ color: '#818cf8' }} />
                    </div>
                    <span className={`text-sm font-semibold transition-colors ${showPrivacyPolicy ? 'text-WHITE' : 'text-WHITE/70'}`}>Privacy Policy</span>
                    {showPrivacyPolicy && <FaArrowRight size={10} className="ml-auto text-WHITE/30 flex-shrink-0" />}
                  </button>
                )}
              </>
            )}
          </nav>

          {/* Pinned CTA buttons at bottom — always visible */}
          <div className="flex-shrink-0 px-5 pb-6 pt-2">
            <div className="relative h-px w-full mb-3">
              <div className="absolute inset-0 bg-WHITE/10" />
              <div className="absolute inset-y-0 left-[10%] right-[10%]" style={{ background: 'linear-gradient(90deg, transparent, rgba(34,197,94,0.35), rgba(59,130,246,0.35), transparent)' }} />
            </div>
            <div className="flex gap-3">
              {/* Download App */}
              {config.sections.showDownload && (
                <button
                  onClick={() => {
                    track({ eventType: 'cta_click', view: getCurrentView(), eventData: { cta: 'download' } });
                    goToView('download');
                    setMobileMenuOpen(false);
                  }}
                  className="group relative flex-1 rounded-2xl overflow-hidden transition-all duration-500 active:scale-[0.97]"
                >
                  <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 0deg, #22c55e, #4ade80, #86efac, #ffffff, #22c55e)', animation: 'ring-spin 3s linear 1 forwards', padding: '1.5px' }}>
                    <div className="w-full h-full rounded-2xl" style={{ background: 'rgba(22,163,74,0.1)' }} />
                  </div>
                  <div className="absolute -inset-1 rounded-2xl opacity-25 pointer-events-none" style={{ background: 'conic-gradient(from 0deg, #22c55e, #4ade80, #22c55e)', filter: 'blur(12px)' }} />
                  <div className="relative flex items-center justify-center gap-2 px-3 py-3.5 rounded-2xl backdrop-blur-2xl" style={{ background: 'rgba(0,0,0,0.55)' }}>
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border"
                      style={{ background: 'rgba(34,197,94,0.2)', borderColor: 'rgba(34,197,94,0.3)' }}
                    >
                      <FaDownload size={13} style={{ color: '#4ade80' }} />
                    </div>
                    <span className="text-xs font-bold tracking-wide" style={{ color: '#4ade80' }}>Download</span>
                  </div>
                </button>
              )}

              {/* Book a Demo */}
              {config.sections.showBookDemo && (
                <button
                  onClick={() => {
                    track({ eventType: 'cta_click', view: getCurrentView(), eventData: { cta: 'bookDemo' } });
                    goToView('demo');
                    setMobileMenuOpen(false);
                  }}
                  className="group relative flex-1 rounded-2xl overflow-hidden transition-all duration-500 active:scale-[0.97]"
                >
                  <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 180deg, #3b82f6, #60a5fa, #93c5fd, #ffffff, #3b82f6)', animation: 'ring-spin 3s linear 1 forwards', padding: '1.5px' }}>
                    <div className="w-full h-full rounded-2xl" style={{ background: 'rgba(59,130,246,0.08)' }} />
                  </div>
                  <div className="absolute -inset-1 rounded-2xl opacity-25 pointer-events-none" style={{ background: 'conic-gradient(from 180deg, #3b82f6, #60a5fa, #3b82f6)', filter: 'blur(12px)' }} />
                  <div className="relative flex items-center justify-center gap-2 px-3 py-3.5 rounded-2xl backdrop-blur-2xl" style={{ background: 'rgba(0,0,0,0.55)' }}>
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border"
                      style={{ background: 'rgba(59,130,246,0.2)', borderColor: 'rgba(59,130,246,0.3)' }}
                    >
                      <FaCalendarCheck size={13} style={{ color: '#60a5fa' }} />
                    </div>
                    <span className="text-xs font-bold tracking-wide" style={{ color: '#60a5fa' }}>Book Demo</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Container */}
      <div className="relative z-10 min-h-screen flex items-start justify-center px-4 lg:px-8">
        <div className="w-full h-full flex flex-col lg:flex-row items-start gap-8">
          
          {/* Left Side - CollectedAF App Section (20%) */}
          <LeftSection 
            onDownloadClick={() => {
              track({ eventType: 'cta_click', view: getCurrentView(), eventData: { cta: 'download' } });
              goToView('download');
            }}
            onBookDemoClick={() => {
              track({ eventType: 'cta_click', view: getCurrentView(), eventData: { cta: 'bookDemo' } });
              goToView('demo');
            }}
            onExperienceClick={() => goToSection(0)}
            onPrivacyPolicyClick={() => goToView('privacy')}
            isDownloadActive={showDownload}
            isBookDemoActive={showBookDemo}
            videoUrl={videoUrl}
            loading={loading}
            config={config}
          />

          {/* Center - Main Content Section (60%) */}
          <div ref={centerSectionRef} className="w-full lg:w-[60%] flex flex-col">
            {/* Mobile Breadcrumb Navigation - Only visible on mobile, navigates to all screens */}
            <div className="lg:hidden mb-4 px-4">
              {(() => {
                const sectionVisibility = [config.sections.showExperience, config.sections.showVenues, config.sections.showCommunity, config.sections.showTechnology, config.sections.showCareers];
                const mobileScreens: { label: string; isActive: boolean; onClick: () => void }[] = [];
                if (config.sections.showHome) {
                  mobileScreens.push({
                    label: 'Home',
                    isActive: showHome,
                    onClick: () => goToView('home'),
                  });
                }
                sections.forEach((section, i) => {
                  if (sectionVisibility[i]) {
                    mobileScreens.push({
                      label: section.title.replace(/^(The |For )/, ''),
                      isActive: !showHome && !showDownload && !showBookDemo && !showPrivacyPolicy && !showChildSafety && !showEvents && activeSection === i,
                      onClick: () => goToSection(i),
                    });
                  }
                });
                mobileScreens.push({
                  label: 'Events',
                  isActive: showEvents,
                  onClick: () => goToView('events'),
                });
                if (config.sections.showDownload) {
                  mobileScreens.push({
                    label: 'Download',
                    isActive: showDownload,
                    onClick: () => goToView('download'),
                  });
                }
                if (config.sections.showBookDemo) {
                  mobileScreens.push({
                    label: 'Book a Demo',
                    isActive: showBookDemo,
                    onClick: () => goToView('demo'),
                  });
                }
                if (config.sections.showPrivacyPolicy) {
                  mobileScreens.push({
                    label: 'Privacy Policy',
                    isActive: showPrivacyPolicy,
                    onClick: () => goToView('privacy'),
                  });
                }
                const currentIndex = mobileScreens.findIndex((s) => s.isActive);
                const currentLabel = currentIndex >= 0 ? mobileScreens[currentIndex].label : mobileScreens[0]?.label ?? '';
                const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % mobileScreens.length : 0;
                const nextLabel = mobileScreens[nextIndex]?.label ?? mobileScreens[0]?.label ?? '';

                if (mobileScreens.length === 0) return null;

                const BREADCRUMB_COLORS = [
                  '#38bdf8',
                  '#60a5fa', '#34d399', '#f472b6', '#fbbf24', '#a78bfa',
                  '#22d3ee',
                  '#4ade80', '#818cf8', '#818cf8',
                ];

                return (
                  <>
                    {/* Current section pill */}
                    <div className="flex justify-center mb-2">
                      <div
                        className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 max-w-full overflow-hidden whitespace-nowrap border border-WHITE/15"
                        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(16px)' }}
                      >
                        <span className="text-[10px] font-bold text-WHITE/90 truncate max-w-[8rem]">{currentLabel}</span>
                        <span className="text-WHITE/30 text-[10px]">→</span>
                        <span className="text-[10px] font-medium text-WHITE/50 truncate max-w-[8rem]">{nextLabel}</span>
                      </div>
                    </div>
                    {/* Progress dots with color */}
                    <div className="flex justify-center gap-1.5">
                      {mobileScreens.map((screen, index) => {
                        const dotColor = BREADCRUMB_COLORS[index % BREADCRUMB_COLORS.length];
                        return (
                          <button
                            key={index}
                            onClick={screen.onClick}
                            className="flex-1 h-2 rounded-full transition-all duration-300"
                            style={{
                              background: screen.isActive ? dotColor : 'rgba(255,255,255,0.15)',
                              boxShadow: screen.isActive ? `0 0 8px ${dotColor}60` : 'none',
                            }}
                            aria-label={screen.label}
                          />
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
            
            <div 
              key={showHome ? 'home' : showDownload ? 'download' : showBookDemo ? 'bookdemo' : showPrivacyPolicy ? 'privacy' : showChildSafety ? 'childSafety' : showEvents ? 'events' : showEventDetail ? `event-${rideEventId}` : showBooked ? 'booked' : `section-${activeSection}`} 
              className="animate-fade-in flex-1"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
            {showChildSafety ? (
              <ChildSafetySection />
            ) : showEvents ? (
              <EventsSection
                onSelectEvent={(eventId: string) => {
                  setRideEventId(eventId);
                  goToView('event', { eventId });
                }}
              />
            ) : showEventDetail && rideEventId ? (
              <EventDetailSection
                eventId={rideEventId}
                onBack={() => goToView('events')}
                onBooked={(state: any) => {
                  setBookingState(state);
                  goToView('booked', { eventId: rideEventId });
                }}
              />
            ) : showBooked ? (
              <BookingConfirmationSection
                bookingState={bookingState}
                onBrowseEvents={() => goToView('events')}
              />
            ) : showPrivacyPolicy ? (
              <PrivacyPolicySection />
            ) : showHome ? (
              <HomeSection
                onDownloadClick={() => goToView('download')}
                onNavigateToExperience={() => goToSection(0)}
                onNavigateToCommunity={() => goToSection(2)}
                onNavigateToTechnology={() => goToSection(3)}
                onNavigateToVenues={() => goToSection(1)}
              />
            ) : showDownload ? (
              <DownloadSection
                config={config.download}
                onTrackDownload={(platform) =>
                  track({ eventType: 'download_link_click', view: 'download', eventData: { platform } })
                }
              />
            ) : showBookDemo ? (
              <BookDemoSection onTrackSubmit={() => track({ eventType: 'demo_form_submit', view: 'demo' })} />
            ) : activeSection === 0 ? (
              <HowItWorksSection />
            ) : showVenues || activeSection === 1 ? (
              <VenuesSection onBookDemoClick={() => goToView('demo')} />
            ) : activeSection === 2 ? (
              <CommunitySection />
            ) : activeSection === 3 ? (
              <TechnologySection />
            ) : activeSection === 4 ? (
              <CareersSection
                onTrackSubmit={() =>
                  track({ eventType: 'cta_click', view: 'main', section: 'careers', eventData: { cta: 'job_application_submit' } })
                }
              />
            ) : (
              <CenterSection activeSection={activeSection} sections={sections} />
            )}
            </div>
          </div>

          {/* Right Side - Navigation & Video Section (20%) */}
          <RightSection 
            activeSection={activeSection} 
            sections={sections}
            setActiveSection={goToSection}
            sectionVisibility={[config.sections.showExperience, config.sections.showVenues, config.sections.showCommunity, config.sections.showTechnology, config.sections.showCareers]}
            videoUrl={videoUrl}
            loading={loading}
            showPrivacyPolicy={showPrivacyPolicy}
            showEvents={showEvents}
            onHomeClick={() => goToView('home')}
            onPrivacyPolicyClick={() => goToView('privacy')}
            onEventsClick={() => goToView('events')}
            config={config}
          />
        </div>
      </div>

      {/* Bottom-Left Version and Support */}
      <div className="absolute bottom-8 left-12 z-10 flex flex-col gap-2">
        <button
          onClick={() => config.sections.showDownload && goToView('download')}
          className="text-BLACK tracking-[0.2em] text-sm font-light hover:text-ORANGE transition-colors cursor-pointer text-left"
        >
          {config.shell.versionText}
        </button>
        <a
          href={`mailto:${config.shell.supportEmail}`}
          className="text-BLACK text-xs font-light hover:text-ORANGE transition-colors"
        >
          {config.shell.supportEmail}
        </a>
        <button
          onClick={() => goToView('childSafety')}
          className="text-BLACK/60 text-[10px] font-light hover:text-ORANGE transition-colors text-left"
        >
          Child Safety Standards
        </button>
      </div>

      {/* Bottom-Right Diagonal Stripes */}
      <div className="absolute bottom-0 right-0 w-96 h-96 overflow-hidden">
        {/* Blue Stripe */}
        <div 
          className="absolute bottom-0 right-0 w-full h-32 origin-bottom-right"
          style={{
            background: '#0066cc',
            transform: 'rotate(-35deg) translateX(50%)',
          }}
        />
        {/* Orange Stripe */}
        <div 
          className="absolute bottom-0 right-0 w-full h-32 origin-bottom-right"
          style={{
            background: '#ff6600',
            transform: 'rotate(-35deg) translateX(50%) translateY(80px)',
          }}
        />
        {/* White Icon Logo */}
        <div className="absolute bottom-8 right-8 bg-white rounded-lg p-2 shadow-lg">
          <img 
            src={appIcon} 
            alt="CollectedAF Icon" 
            className="w-12 h-12"
          />
        </div>
      </div>
    </div>
  );
};

export default CollectedAFWebSite;
